import {
  escapeHtml,
  formatDeliveryDeadlineDisplay,
  formatLn2DropOffDateLine,
  formatPickupDeliverySection,
  formatPickupDeliveryTableHtml,
  formatQuickstatDeliveryCityDisplay,
  formatQuickstatDeliveryStateOrCountryDisplay,
  LEG2_EMAIL_PLACEHOLDER,
  type QuickstatBookingEmailSource,
} from './quickstatBookingEmailFormat';

const HTML_WRAP_OPEN =
  '<div style="font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#000;">';

const HTML_WRAP_CLOSE = '</div>';

/**
 * Use `<div>` + padding-bottom (not `<p>` margins): Ant/layout CSS often zeros `p`
 * margins, which hides the gap after “Hello QuickStat team,” in the modal preview.
 * Padding doesn’t collapse, so spacing stays visible in-browser and pastes cleanly.
 */
function htmlDiv(bottomSpacingPx: number, innerHtml: string): string {
  return `<div style="display:block;margin:0;padding:0 0 ${bottomSpacingPx}px 0;line-height:1.45;">${innerHtml}</div>`;
}

function htmlDivNoSpacing(innerHtml: string): string {
  return `<div style="display:block;margin:0;padding:0;line-height:1.45;">${innerHtml}</div>`;
}

export const QUICKSTAT_EMAIL_TEMPLATE_OPTIONS = [
  { id: 'credo_obc', label: 'Credo OBC' },
  { id: 'credo_drive_only', label: 'Credo Drive Only' },
  {
    id: 'ln2_distributor_drive_only',
    label: 'LN2 Specialty Distributor Drive Only',
  },
  {
    id: 'ln2_distributor_flight_only',
    label: 'LN2 Specialty Distributor Flight Only',
  },
  {
    id: 'ln2_pharmacy_drive_only',
    label: 'LN2 Specialty Pharmacy Drive Only',
  },
  {
    id: 'ln2_pharmacy_flight_drive_combo',
    label: 'LN2 Specialty Pharmacy Flight/Drive Combo',
  },
] as const;

export type QuickstatEmailTemplateId =
  (typeof QUICKSTAT_EMAIL_TEMPLATE_OPTIONS)[number]['id'];

/** Second AWB placeholder (LN2 pharmacy multi-leg templates). */
const LN2_SECOND_AWB_PLACEHOLDER = '[Second AWB]';

/** Manual placeholder label for Leg 2 details (LN2 multi-leg templates; plain + HTML body). */
const LN2_PHARMACY_DRIVE_ONLY_FINAL_DEST_PLACEHOLDER = '[Final Destination]';

/** Rich-text emphasis for manual placeholders in LN2 pharmacy drive only HTML (Outlook-style). */
const LN2_PLACEHOLDER_FINAL_DEST_HTML = `<strong><em>${LN2_PHARMACY_DRIVE_ONLY_FINAL_DEST_PLACEHOLDER}</em></strong>`;
const LN2_PLACEHOLDER_SECOND_AWB_HTML =
  '<strong><em>[Second AWB]</em></strong>';

/** Leg 2 block in LN2 HTML bodies — matches {@link LEG2_EMAIL_PLACEHOLDER} with Outlook-style emphasis. */
const LN2_LEG2_HTML = `<strong><em>${escapeHtml(LEG2_EMAIL_PLACEHOLDER)}</em></strong>`;

/** Intro block for LN2 Specialty Distributor Flight Only — pickup to delivery facility names. */
function ln2DistributorFlightOnlyIntro(
  booking: QuickstatBookingEmailSource
): string {
  const pickupName = (booking.pickup_name ?? '').trim() || '[Pickup name]';
  const destName = (booking.delivery_name ?? '').trim() || '[Destination name]';
  return [
    'Please see the following AWB for an LN2 shipment I booked:',
    `- From ${pickupName} to ${destName}`,
  ].join('\n');
}

function ln2DistributorFlightOnlyIntroHtml(
  booking: QuickstatBookingEmailSource
): string {
  const pickupName = escapeHtml(
    (booking.pickup_name ?? '').trim() || '[Pickup name]'
  );
  const destName = escapeHtml(
    (booking.delivery_name ?? '').trim() || '[Destination name]'
  );
  return [
    'Please see the following AWB for an LN2 shipment I booked:',
    `- From ${pickupName} to ${destName}`,
  ].join('<br />\n');
}

/** Intro block for LN2 Specialty Pharmacy Drive Only — bullets from pickup / delivery facility names. */
function ln2PharmacyDriveOnlyLegIntro(
  booking: QuickstatBookingEmailSource
): string {
  const pickupName = (booking.pickup_name ?? '').trim();
  const destName = (booking.delivery_name ?? '').trim();
  const pu = pickupName || '[Pickup name]';
  const dest = destName || '[Destination name]';
  return [
    'Please see the following AWB for an LN2 shipment I booked that has two legs to it:',
    `- Leg 1: ${pu}-${dest}`,
    `- Leg 2: ${dest}- ${LN2_PHARMACY_DRIVE_ONLY_FINAL_DEST_PLACEHOLDER}.`,
  ].join('\n');
}

function ln2PharmacyDriveOnlyLegIntroHtml(
  booking: QuickstatBookingEmailSource
): string {
  const pickupName = (booking.pickup_name ?? '').trim();
  const destName = (booking.delivery_name ?? '').trim();
  const pu = escapeHtml(pickupName || '[Pickup name]');
  const dest = escapeHtml(destName || '[Destination name]');
  return [
    'Please see the following AWB for an LN2 shipment I booked that has two legs to it:',
    `- Leg 1: ${pu}-${dest}`,
    `- Leg 2: ${dest}- ${LN2_PLACEHOLDER_FINAL_DEST_HTML}.`,
  ].join('<br />\n');
}

function ln2PharmacyFlightDriveLocationPartsPlain(
  booking: QuickstatBookingEmailSource
): { destination: string; city: string; state: string } {
  const destination =
    (booking.delivery_name ?? '').trim() || '[destination location]';
  const city =
    formatQuickstatDeliveryCityDisplay(booking.delivery_city) ||
    '[delivery city]';
  const state =
    formatQuickstatDeliveryStateOrCountryDisplay(booking) || '[delivery state]';
  return { destination, city, state };
}

function ln2PharmacyFlightDriveComboIntroPlain(
  awb: string,
  booking: QuickstatBookingEmailSource
): string {
  const a = (awb || '').trim() || '[AWB — pending]';
  const { destination, city, state } =
    ln2PharmacyFlightDriveLocationPartsPlain(booking);
  return [
    `I have placed the following orders under AWB #'s ${a} AND ${LN2_SECOND_AWB_PLACEHOLDER}.`,
    `- The first leg of this shipment will be a flight to ${destination} in ${city}, ${state}`,
    `- From there the second leg will be a drive only same day to ${LN2_PHARMACY_DRIVE_ONLY_FINAL_DEST_PLACEHOLDER}`,
  ].join('\n');
}

function ln2PharmacyFlightDriveComboIntroHtml(
  awb: string,
  booking: QuickstatBookingEmailSource
): string {
  const awbH = escapeHtml((awb || '').trim() || '[AWB — pending]');
  const destRaw = (booking.delivery_name ?? '').trim();
  const cityRaw = formatQuickstatDeliveryCityDisplay(booking.delivery_city);
  const stateOrCountryRaw =
    formatQuickstatDeliveryStateOrCountryDisplay(booking);

  const destHtml =
    destRaw.length > 0
      ? escapeHtml(destRaw)
      : `<strong><em>${escapeHtml('[destination location]')}</em></strong>`;
  const cityHtml =
    cityRaw.length > 0
      ? escapeHtml(cityRaw)
      : `<strong><em>${escapeHtml('[delivery city]')}</em></strong>`;
  const stateHtml =
    stateOrCountryRaw.length > 0
      ? escapeHtml(stateOrCountryRaw)
      : `<strong><em>${escapeHtml('[delivery state]')}</em></strong>`;

  return [
    `I have placed the following orders under AWB #'s ${awbH} AND ${LN2_PLACEHOLDER_SECOND_AWB_HTML}.`,
    `- The first leg of this shipment will be a flight to ${destHtml} in ${cityHtml}, ${stateHtml}`,
    `- From there the second leg will be a drive only same day to ${LN2_PLACEHOLDER_FINAL_DEST_HTML}`,
  ].join('<br />\n');
}

/** Shipper tag shown in the subject, fixed by the email template the user picks. */
const SHIPPER_BY_TEMPLATE: Record<QuickstatEmailTemplateId, 'Credo' | 'LN2'> = {
  credo_obc: 'Credo',
  credo_drive_only: 'Credo',
  ln2_distributor_drive_only: 'LN2',
  ln2_distributor_flight_only: 'LN2',
  ln2_pharmacy_drive_only: 'LN2',
  ln2_pharmacy_flight_drive_combo: 'LN2',
};

export function buildQuickstatBookingEmailSubject(
  companyName: string,
  awb: string,
  booking: QuickstatBookingEmailSource,
  templateId: QuickstatEmailTemplateId
): string {
  const co = (companyName || '').trim();
  const a = (awb || '').trim();
  const delivery = formatDeliveryDeadlineDisplay(booking);
  const shipper = SHIPPER_BY_TEMPLATE[templateId];
  const tail = `AWB # ${a} – NEW ${shipper.toUpperCase()} SHIPMENT BOOKED – Delivery Date: ${delivery}`;
  return co ? `${co} ${tail}` : tail;
}

export function buildQuickstatBookingEmailBody(
  templateId: QuickstatEmailTemplateId,
  awb: string,
  booking: QuickstatBookingEmailSource
): string {
  const leg1 = formatPickupDeliverySection(booking);
  const ln2DropOff = formatLn2DropOffDateLine(booking);
  const a = (awb || '').trim() || '[AWB — pending]';

  switch (templateId) {
    case 'credo_obc':
      return [
        'Hello QuickStat team,',
        '',
        `I have booked the below order under the AWB# ${a}. This shipment will require an OBC, please advise on 3 lanes for this shipment and I will provide the TSA letter for the OBC when obtained.`,
        '',
        leg1,
        '',
        'Thank you!',
      ].join('\n');

    case 'credo_drive_only':
      return [
        'Hello QuickStat team,',
        '',
        `I have placed the below order under AWB # ${a}. This is a DRIVE ONLY, please ensure there is a GPS tracker included.`,
        '',
        leg1,
        '',
        'Thank you!',
      ].join('\n');

    case 'ln2_distributor_drive_only':
      return [
        'Hello QuickStat Team,',
        '',
        `I have placed the below order for an LN2 Shipment under AWB # ${a}. This shipment is a DRIVE ONLY.`,
        '',
        ln2DropOff,
        '',
        leg1,
        '',
        'Thank you!',
      ].join('\n');

    case 'ln2_distributor_flight_only':
      return [
        'Hello QuickStat team,',
        '',
        ln2DistributorFlightOnlyIntro(booking),
        '',
        `AWB: ${a}`,
        ln2DropOff,
        '',
        leg1,
        '',
        'Thank you!',
      ].join('\n');

    case 'ln2_pharmacy_drive_only':
      return [
        'Hello QuickStat team,',
        '',
        ln2PharmacyDriveOnlyLegIntro(booking),
        '',
        'Both legs are DRIVE ONLY',
        '',
        '',
        `AWB: ${a} AND ${LN2_SECOND_AWB_PLACEHOLDER}`,
        '',
        ln2DropOff,
        '',
        leg1,
        '',
        LEG2_EMAIL_PLACEHOLDER,
        '',
        'Thank you!',
      ].join('\n');

    case 'ln2_pharmacy_flight_drive_combo':
      return [
        'Hello QuickStat Team,',
        '',
        ln2PharmacyFlightDriveComboIntroPlain(a, booking),
        '',
        ln2DropOff,
        '',
        leg1,
        '',
        LEG2_EMAIL_PLACEHOLDER,
        '',
        'Thank you!',
      ].join('\n');

    default: {
      const _exhaustive: never = templateId;
      return _exhaustive;
    }
  }
}

/**
 * Rich email body for paste into Outlook / clients that read `text/html` from the clipboard.
 * Plain-text fallback: {@link buildQuickstatBookingEmailBody}.
 */
export function buildQuickstatBookingEmailBodyHtml(
  templateId: QuickstatEmailTemplateId,
  awb: string,
  booking: QuickstatBookingEmailSource
): string {
  const legTable = formatPickupDeliveryTableHtml(booking);
  const ln2DropOffHtml = htmlDiv(
    14,
    escapeHtml(formatLn2DropOffDateLine(booking))
  );
  const a = (awb || '').trim() || '[AWB — pending]';
  const awbH = escapeHtml(a);

  switch (templateId) {
    case 'credo_obc':
      return `${HTML_WRAP_OPEN}${htmlDiv(16, 'Hello QuickStat team,')}${htmlDiv(
        14,
        `I have booked the below order under the AWB# ${awbH}. This shipment will require an OBC, please advise on 3 lanes for this shipment and I will provide the TSA letter for the OBC when obtained.`
      )}${legTable}${htmlDivNoSpacing('Thank you!')}${HTML_WRAP_CLOSE}`;

    case 'credo_drive_only':
      return `${HTML_WRAP_OPEN}${htmlDiv(16, 'Hello QuickStat team,')}${htmlDiv(
        14,
        `I have placed the below order under AWB # ${awbH}. This is a DRIVE ONLY, please ensure there is a GPS tracker included.`
      )}${legTable}${htmlDivNoSpacing('Thank you!')}${HTML_WRAP_CLOSE}`;

    case 'ln2_distributor_drive_only':
      return `${HTML_WRAP_OPEN}${htmlDiv(16, 'Hello QuickStat Team,')}${htmlDiv(
        14,
        `I have placed the below order for an LN2 Shipment under AWB # ${awbH}. This shipment is a DRIVE ONLY.`
      )}${ln2DropOffHtml}${legTable}${htmlDivNoSpacing('Thank you!')}${HTML_WRAP_CLOSE}`;

    case 'ln2_distributor_flight_only':
      return `${HTML_WRAP_OPEN}${htmlDiv(16, 'Hello QuickStat team,')}${htmlDiv(
        14,
        ln2DistributorFlightOnlyIntroHtml(booking)
      )}${htmlDiv(8, `AWB: ${awbH}`)}${ln2DropOffHtml}${legTable}${htmlDivNoSpacing('Thank you!')}${HTML_WRAP_CLOSE}`;

    case 'ln2_pharmacy_drive_only':
      return `${HTML_WRAP_OPEN}${htmlDiv(
        16,
        'Hello QuickStat team,'
      )}${htmlDiv(14, ln2PharmacyDriveOnlyLegIntroHtml(booking))}${htmlDiv(
        22,
        'Both legs are DRIVE ONLY'
      )}${htmlDiv(
        14,
        `AWB: ${awbH} AND ${LN2_PLACEHOLDER_SECOND_AWB_HTML}`
      )}${ln2DropOffHtml}${legTable}<div style="margin:12px 0;">${LN2_LEG2_HTML}</div><div style="display:block;margin:0;padding:12px 0 0;line-height:1.45;">Thank you!</div>${HTML_WRAP_CLOSE}`;

    case 'ln2_pharmacy_flight_drive_combo':
      return `${HTML_WRAP_OPEN}${htmlDiv(16, 'Hello QuickStat Team,')}${htmlDiv(
        14,
        ln2PharmacyFlightDriveComboIntroHtml(awb, booking)
      )}${ln2DropOffHtml}${legTable}<div style="margin:12px 0;">${LN2_LEG2_HTML}</div><div style="display:block;margin:0;padding:12px 0 0;line-height:1.45;">Thank you!</div>${HTML_WRAP_CLOSE}`;

    default: {
      const _exhaustive: never = templateId;
      return _exhaustive;
    }
  }
}
